import { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Dimensions,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import { TrendingUp, Award, Calendar, BarChart3, ChevronDown, Zap, Dumbbell, Lock, Trophy, Activity, Target, Clock, Flame } from 'lucide-react-native';
import PRProgressionChart from './PRProgressionChart';
import { getTrainingSplit, type TrainingSplitResult } from '@/lib/trainingSplit';
import { hasAtLeast, type Tier } from '@/lib/subscription';
import { Svg, Polyline, Circle, Line, Text as SvgText } from 'react-native-svg';
import EmptyState from './EmptyState';
import { calculateE1RM } from '@/lib/lifts/canonical';
import { lookupExerciseInfo } from '@/constants/exerciseDatabase';
import SkeletonRow from './SkeletonRow';
import PaywallGate from './PaywallGate';
import { useRouter } from 'expo-router';
import { TimeRange, getAnalyticsDateRange, getTimeRangeLabel } from '@/lib/analyticsRange';
import { getUserDuelStats, type DuelStats } from '@/lib/duelStats';
import { calculateWorkoutVolume, convertWeight } from '@/lib/volumeCalc';
import { roundDisplay } from '@/lib/useUnits';
import TutorialOverlay from '@/components/TutorialOverlay';

type MetricType = 'topSet' | 'estimated1RM' | 'totalVolume';

interface AnalyticsData {
  totalWorkouts: number;
  totalVolume: number;
  totalExercises: number;
  volumeByDay: { date: string; volume: number }[];
  exerciseList: string[];
  avgRPE: number | null;
  workoutsThisWeek: number;
  workoutsThisMonth: number;
  recentPRs: PREntry[];
  exerciseFrequency: { name: string; count: number }[];
}

interface PREntry {
  exercise: string;
  weight: number;
  reps: number;
  date: string;
}

interface ExerciseDataPoint {
  date: string;
  value: number;
}

export default function Analytics() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { isProPlus, tier } = useSubscription();
  const router = useRouter();
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('topSet');
  const [exerciseData, setExerciseData] = useState<ExerciseDataPoint[]>([]);
  const [showExercisePicker, setShowExercisePicker] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [allExerciseNames, setAllExerciseNames] = useState<string[]>([]);
  const [duelStats, setDuelStats] = useState<DuelStats | null>(null);
  const [trainingSplit, setTrainingSplit] = useState<TrainingSplitResult[]>([]);

  // Free analytics data (always last 30 days, independent of timeRange)
  const [freeData, setFreeData] = useState<AnalyticsData | null>(null);

  const screenWidth = Dimensions.get('window').width;
  const chartWidth = screenWidth - 64;
  const chartHeight = 220;
  const c = theme.colors;

  const displayUnits = profile?.units ?? 'lbs';

  // ── Load free analytics (last 30 days, no tier gate) ──
  useEffect(() => {
    loadFreeAnalytics();
  }, [profile?.id]);

  // ── Pro: time-range-dependent data ──
  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    if (!isProPlus) return;
    const skip = hasLoadedOnce.current;
    hasLoadedOnce.current = true;
    loadProAnalytics(skip);
  }, [timeRange, isProPlus]);

  useEffect(() => {
    if (!isProPlus) return;
    if (selectedExercise) loadExerciseData();
  }, [selectedExercise, selectedMetric, timeRange, isProPlus]);

  // Fetch all exercise names
  useEffect(() => {
    if (!isProPlus) return;
    (async () => {
      const { data: exData } = await supabase.from('exercises').select('name').order('name');
      if (exData) setAllExerciseNames(exData.map((e: any) => e.name));
    })();
  }, [isProPlus]);

  // Load duel stats + training split once
  useEffect(() => {
    if (profile?.id && isProPlus) getUserDuelStats(profile.id).then(setDuelStats);
    if (profile?.id) getTrainingSplit(profile.id, 30).then(setTrainingSplit).catch(() => {});
  }, [profile?.id, isProPlus]);

  const loadFreeAnalytics = async () => {
    if (!profile?.id) return;
    setLoading(true);

    // Fetch ALL workouts for the user (for summary + PRs + frequency)
    const { data: allWorkouts } = await supabase
      .from('workouts')
      .select('*')
      .eq('user_id', profile.id)
      .order('performed_at', { ascending: true });

    if (!allWorkouts || allWorkouts.length === 0) {
      setFreeData({
        totalWorkouts: 0, totalVolume: 0, totalExercises: 0,
        volumeByDay: [], exerciseList: [], avgRPE: null,
        workoutsThisWeek: 0, workoutsThisMonth: 0,
        recentPRs: [], exerciseFrequency: [],
      });
      setLoading(false);
      return;
    }

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    let totalVolume = 0;
    let totalExercises = 0;
    const exerciseSet = new Set<string>();
    const volumeByDay: Record<string, number> = {};
    let workoutsThisWeek = 0;
    let workoutsThisMonth = 0;

    // PR tracking: best weight per exercise
    const prMap: Record<string, { weight: number; reps: number; date: string }> = {};
    // Exercise frequency
    const freqMap: Record<string, number> = {};

    allWorkouts.forEach((workout: any) => {
      const perfDate = new Date(workout.performed_at);
      const date = perfDate.toISOString().split('T')[0];
      const payload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;
      const workoutUnits: string = payload?.units ?? 'lbs';

      if (perfDate >= weekAgo) workoutsThisWeek++;
      if (perfDate >= monthStart) workoutsThisMonth++;

      const vol = calculateWorkoutVolume(workout.payload, displayUnits);
      totalVolume += vol;

      // Only include last 30 days in the volume chart
      if (perfDate >= thirtyDaysAgo) {
        volumeByDay[date] = (volumeByDay[date] || 0) + vol;
      }

      (payload?.exercises ?? []).forEach((exercise: any) => {
        const exName = exercise.name;
        exerciseSet.add(exName);
        totalExercises++;
        freqMap[exName] = (freqMap[exName] || 0) + 1;

        (exercise.sets ?? []).forEach((set: any) => {
          const rawWeight = set.weight || 0;
          const weight = roundDisplay(convertWeight(rawWeight, workoutUnits, displayUnits));
          const reps = set.reps || 0;
          if (!prMap[exName] || weight > prMap[exName].weight) {
            prMap[exName] = { weight, reps, date };
          }
        });
      });
    });

    // Recent PRs: sort by date descending, take 5
    const recentPRs: PREntry[] = Object.entries(prMap)
      .map(([exercise, pr]) => ({ exercise, ...pr }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);

    // Exercise frequency: top 5
    const exerciseFrequency = Object.entries(freqMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Auto-select most logged exercise for Pro (if not already selected)
    if (exerciseFrequency.length > 0 && !selectedExercise) {
      setSelectedExercise(exerciseFrequency[0].name);
    }

    const result: AnalyticsData = {
      totalWorkouts: allWorkouts.length,
      totalVolume,
      totalExercises,
      volumeByDay: Object.entries(volumeByDay)
        .map(([d, volume]) => ({ date: d, volume }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
      exerciseList: Array.from(exerciseSet).sort(),
      avgRPE: null,
      workoutsThisWeek,
      workoutsThisMonth,
      recentPRs,
      exerciseFrequency,
    };

    setFreeData(result);
    setLoading(false);
  };

  const loadProAnalytics = async (skipLoadingState = false) => {
    if (!profile?.id) return;
    if (!skipLoadingState) setLoading(true);
    const { startUtcIso, endUtcIsoExclusive } = getAnalyticsDateRange(timeRange);

    let query = supabase.from('workouts').select('*').eq('user_id', profile.id);
    if (startUtcIso) query = query.gte('performed_at', startUtcIso);
    query = query.lt('performed_at', endUtcIsoExclusive);

    const { data: workouts } = await query.order('performed_at', { ascending: true });
    if (!workouts || workouts.length === 0) {
      setData({
        totalWorkouts: 0, totalVolume: 0, totalExercises: 0,
        volumeByDay: [], exerciseList: [], avgRPE: null,
        workoutsThisWeek: 0, workoutsThisMonth: 0,
        recentPRs: [], exerciseFrequency: [],
      });
      setLoading(false);
      return;
    }

    const volumeByDay: Record<string, number> = {};
    let totalVolume = 0;
    let totalExercises = 0;
    const exerciseSet = new Set<string>();
    let rpeSum = 0;
    let rpeCount = 0;

    workouts.forEach((workout: any) => {
      const date = new Date(workout.performed_at).toISOString().split('T')[0];
      const payload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;

      if (typeof payload?.rpe === 'number') { rpeSum += payload.rpe; rpeCount++; }

      (payload?.exercises ?? []).forEach((exercise: any) => {
        exerciseSet.add(exercise.name);
        totalExercises++;
        if (typeof exercise.rpe === 'number') { rpeSum += exercise.rpe; rpeCount++; }
      });

      const vol = calculateWorkoutVolume(workout.payload, displayUnits);
      volumeByDay[date] = (volumeByDay[date] || 0) + vol;
      totalVolume += vol;
    });

    setData({
      totalWorkouts: workouts.length,
      totalVolume,
      totalExercises,
      volumeByDay: Object.entries(volumeByDay).map(([date, volume]) => ({ date, volume })),
      exerciseList: Array.from(exerciseSet).sort(),
      avgRPE: rpeCount > 0 ? Math.round((rpeSum / rpeCount) * 10) / 10 : null,
      workoutsThisWeek: 0,
      workoutsThisMonth: 0,
      recentPRs: [],
      exerciseFrequency: [],
    });

    if (exerciseSet.size > 0 && !selectedExercise) {
      setSelectedExercise(Array.from(exerciseSet)[0]);
    }
    setLoading(false);
  };

  const loadExerciseData = async () => {
    if (!profile?.id) return;
    const { startUtcIso, endUtcIsoExclusive } = getAnalyticsDateRange(timeRange);
    let query = supabase.from('workouts').select('*').eq('user_id', profile.id);
    if (startUtcIso) query = query.gte('performed_at', startUtcIso);
    query = query.lt('performed_at', endUtcIsoExclusive);
    const { data: workouts } = await query.order('performed_at', { ascending: true });
    if (!workouts) return;

    const dataPoints: Record<string, number> = {};
    workouts.forEach((workout: any) => {
      const date = new Date(workout.performed_at).toISOString().split('T')[0];
      const payload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;
      const workoutUnits: string = payload?.units ?? 'lbs';
      (payload?.exercises ?? []).forEach((exercise: any) => {
        if (exercise.name === selectedExercise) {
          (exercise.sets ?? []).forEach((set: any) => {
            const rawWeight = set.weight || 0;
            const weight = roundDisplay(convertWeight(rawWeight, workoutUnits, displayUnits));
            const reps = set.reps || 0;
            let value = 0;
            switch (selectedMetric) {
              case 'topSet': value = weight; break;
              case 'estimated1RM': value = roundDisplay(calculateE1RM(weight, reps)); break;
              case 'totalVolume': value = roundDisplay(weight * reps); break;
            }
            if (!dataPoints[date] || value > dataPoints[date]) dataPoints[date] = value;
          });
        }
      });
    });

    setExerciseData(
      Object.entries(dataPoints)
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    );
  };

  const formatChartDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    const mon = d.toLocaleDateString('en-US', { month: 'short' });
    const day = d.getDate();
    if (timeRange === 'lifetime') return `${mon} ${day}, ${d.getFullYear()}`;
    return `${mon} ${day}`;
  };

  const formatAxisValue = (v: number): string => {
    if (v === 0) return '0';
    if (v >= 1000000) { const n = v / 1000000; return n % 1 === 0 ? `${n}M` : `${n.toFixed(1)}M`; }
    if (v >= 1000) { const n = v / 1000; return n % 1 === 0 ? `${n}K` : `${n.toFixed(1)}K`; }
    return `${Math.round(v)}`;
  };

  const renderLineChart = (dataPoints: ExerciseDataPoint[], title: string, color: string) => {
    if (dataPoints.length === 0) {
      return (
        <View style={styles.chartContainer}>
          {title ? <Text style={[styles.chartTitle, { color: c.text }]}>{title}</Text> : null}
          <View style={styles.emptyChartContainer}>
            <Text style={[styles.emptyChartText, { color: c.textSecondary }]}>No data in this timeframe</Text>
          </View>
        </View>
      );
    }

    const rawMax = Math.max(...dataPoints.map(d => d.value));
    const niceStep = (() => {
      if (rawMax <= 0) return 100;
      const rough = rawMax / 5;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
      const normalized = rough / magnitude;
      if (normalized <= 1) return magnitude;
      if (normalized <= 2) return 2 * magnitude;
      if (normalized <= 5) return 5 * magnitude;
      return 10 * magnitude;
    })();
    const niceMax = Math.ceil(rawMax / niceStep) * niceStep || niceStep;
    const stepCount = Math.round(niceMax / niceStep);
    const minValue = 0;
    const maxValue = niceMax;
    const range = maxValue - minValue || 1;
    const padL = 50;
    const padR = 16;
    const padT = 20;
    const padB = 36;
    const chartInnerWidth = chartWidth - padL - padR;
    const chartInnerHeight = chartHeight - padT - padB;

    const gridLines = Array.from({ length: stepCount + 1 }, (_, i) => {
      const val = niceStep * i;
      const y = chartHeight - padB - ((val / range) * chartInnerHeight);
      return { val, y };
    });

    const points = dataPoints.map((point, index) => {
      const x = padL + (index / Math.max(dataPoints.length - 1, 1)) * chartInnerWidth;
      const y = chartHeight - padB - ((point.value - minValue) / range) * chartInnerHeight;
      return { x, y, value: point.value };
    });

    const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

    const dateLabels: { x: number; label: string; anchor: 'start' | 'middle' | 'end' }[] = [];
    if (dataPoints.length >= 1) dateLabels.push({ x: padL, label: formatChartDate(dataPoints[0].date), anchor: 'start' });
    if (dataPoints.length >= 3) {
      const midIdx = Math.floor(dataPoints.length / 2);
      dateLabels.push({ x: points[midIdx].x, label: formatChartDate(dataPoints[midIdx].date), anchor: 'middle' });
    }
    if (dataPoints.length >= 2) dateLabels.push({ x: padL + chartInnerWidth, label: formatChartDate(dataPoints[dataPoints.length - 1].date), anchor: 'end' });

    return (
      <View style={styles.chartContainer}>
        {title ? <Text style={[styles.chartTitle, { color: c.text }]}>{title}</Text> : null}
        <Svg width={chartWidth} height={chartHeight}>
          {gridLines.map((gl, i) => (
            <Line key={`g${i}`} x1={padL} y1={gl.y} x2={padL + chartInnerWidth} y2={gl.y} stroke={c.border} strokeWidth={1} strokeDasharray={i === 0 ? undefined : '4,4'} />
          ))}
          {gridLines.map((gl, i) => (
            <SvgText key={`y${i}`} x={padL - 6} y={gl.y + 4} fontSize="11" fill={c.textTertiary} textAnchor="end">{formatAxisValue(gl.val)}</SvgText>
          ))}
          {points.length > 1 && (
            <Polyline points={polylinePoints} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          )}
          {points.map((point, index) => (
            <Circle key={index} cx={point.x} cy={point.y} r={3.5} fill={color} stroke={c.surface} strokeWidth={2} />
          ))}
          {dateLabels.map((dl, i) => (
            <SvgText key={`d${i}`} x={dl.x} y={chartHeight - padB + 20} fontSize="11" fill={c.textTertiary} textAnchor={dl.anchor}>{dl.label}</SvgText>
          ))}
        </Svg>
      </View>
    );
  };

  // ── LOADING ──
  if (loading) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: c.background, padding: 24 }]}>
        <SkeletonRow rows={6} />
      </View>
    );
  }

  // ── EMPTY STATE ──
  if (!freeData || freeData.totalWorkouts === 0) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: c.background }]}>
        <EmptyState
          icon={BarChart3}
          title="No Analytics Yet"
          description="Log a workout to begin tracking volume trends, PR history, and training insights."
          actionLabel="Log Workout"
          onAction={() => router.push({ pathname: '/(tabs)/record', params: { view: 'log' } })}
        />
      </View>
    );
  }

  const strengthKg = profile?.duel_standard_strength_kg;
  const hasStrength = typeof strengthKg === 'number' && isFinite(strengthKg) && strengthKg > 0;

  return (
    <ScrollView style={[styles.container, { backgroundColor: c.background }]} contentContainerStyle={styles.content}>

      {/* ════════════════════════════════════════════════════
           FREE ANALYTICS — always visible
         ════════════════════════════════════════════════════ */}

      {/* ── WORKOUT SUMMARY ── */}
      <View style={styles.statsGrid}>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Calendar size={22} color={c.accent} />
          <Text style={[styles.statValue, { color: c.text }]}>{freeData.totalWorkouts}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total Workouts</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <TrendingUp size={22} color={c.accent} />
          <Text style={[styles.statValue, { color: c.text }]}>{Math.round(freeData.totalVolume).toLocaleString()}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total Volume ({displayUnits})</Text>
        </View>
      </View>
      <View style={styles.statsGrid}>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Zap size={22} color={c.accent} />
          <Text style={[styles.statValue, { color: c.text }]}>{freeData.workoutsThisWeek}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>This Week</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Calendar size={22} color={c.accent} />
          <Text style={[styles.statValue, { color: c.text }]}>{freeData.workoutsThisMonth}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>This Month</Text>
        </View>
      </View>

      {/* ── VOLUME TREND (last 30 days) ── */}
      <View style={[styles.chartCard, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <Text style={[styles.chartTitle, { color: c.text, marginBottom: 0 }]}>Volume Trend</Text>
          <Text style={{ fontSize: 12, color: c.textTertiary }}>Last 30 days</Text>
        </View>
        {renderLineChart(
          freeData.volumeByDay.map(item => ({ date: item.date, value: item.volume })),
          '',
          c.accent,
        )}
      </View>

      {/* ── RECENT PRs ── */}
      {freeData.recentPRs.length > 0 && (
        <View style={[styles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={styles.sectionHeader}>
            <Award size={18} color={c.warning} />
            <Text style={[styles.sectionTitle, { color: c.text }]}>Recent PRs</Text>
          </View>
          {freeData.recentPRs.map((pr, i) => (
            <View key={i} style={[styles.prRow, { borderBottomColor: c.border }]}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.prExercise, { color: c.text }]}>{pr.exercise}</Text>
                <Text style={[styles.prDate, { color: c.textTertiary }]}>{(() => {
                  const today = new Date();
                  const prDay = new Date(pr.date);
                  if (
                    prDay.getFullYear() === today.getFullYear() &&
                    prDay.getMonth() === today.getMonth() &&
                    prDay.getDate() === today.getDate()
                  ) return 'Today';
                  return prDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                })()}</Text>
              </View>
              <Text style={[styles.prValue, { color: c.accent }]}>
                {Math.round(pr.weight)} {displayUnits} × {pr.reps}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* ── EXERCISE FREQUENCY ── */}
      {freeData.exerciseFrequency.length > 0 && (
        <View style={[styles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={styles.sectionHeader}>
            <Dumbbell size={18} color={c.accent} />
            <Text style={[styles.sectionTitle, { color: c.text }]}>Most Logged Exercises</Text>
          </View>
          {freeData.exerciseFrequency.map((ex, i) => (
            <View key={i} style={[styles.freqRow, { borderBottomColor: c.border }]}>
              <Text style={[styles.freqName, { color: c.text }]}>{ex.name}</Text>
              <View style={[styles.freqBadge, { backgroundColor: c.surfaceVariant }]}>
                <Text style={[styles.freqCount, { color: c.textSecondary }]}>{ex.count}×</Text>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* ════════════════════════════════════════════════════
           PRO ANALYTICS — behind paywall
         ════════════════════════════════════════════════════ */}

      <PaywallGate
        requiredTier="pro"
        title="Pro Analytics"
        body="Exercise deep dive, duel stats, RPE trends, training split analysis, and data export."
      >
        {isProPlus && freeData.totalWorkouts < 4 ? (
          <View style={{ alignItems: 'center', paddingVertical: 40, paddingHorizontal: 24 }}>
            <Text style={{ fontSize: 20, fontWeight: '900', color: c.accent, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12, textAlign: 'center' }}>
              YOUR ANALYTICS AWAIT
            </Text>
            <Text style={{ fontSize: 14, color: c.textSecondary, textAlign: 'center', lineHeight: 22, marginBottom: 24 }}>
              Log at least 4 workouts to unlock your performance trends, volume analysis, and PR velocity.
            </Text>
            <TouchableOpacity
              style={{ backgroundColor: c.accent, paddingVertical: 14, paddingHorizontal: 32, borderRadius: 2 }}
              onPress={() => router.push('/(tabs)/record')}
            >
              <Text style={{ fontSize: 14, fontWeight: '800', color: c.accentForeground, textTransform: 'uppercase', letterSpacing: 1 }}>
                LOG A WORKOUT
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
        <ProAnalyticsSection
          data={data}
          freeData={freeData}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          selectedExercise={selectedExercise}
          setSelectedExercise={setSelectedExercise}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          exerciseData={exerciseData}
          showExercisePicker={showExercisePicker}
          setShowExercisePicker={setShowExercisePicker}
          exerciseSearch={exerciseSearch}
          setExerciseSearch={setExerciseSearch}
          allExerciseNames={allExerciseNames}
          duelStats={duelStats}
          strengthKg={strengthKg}
          hasStrength={hasStrength}
          displayUnits={displayUnits}
          renderLineChart={renderLineChart}
          c={c}
          chartWidth={chartWidth}
          chartHeight={chartHeight}
          profileId={profile?.id ?? null}
          tier={tier}
          trainingSplit={trainingSplit}
        />
        )}
      </PaywallGate>

      {/* ── LOG WORKOUT CTA ── */}
      <TouchableOpacity
        style={[styles.logWorkoutButton, { backgroundColor: c.accent }]}
        onPress={() => router.push({ pathname: '/(tabs)/record', params: { view: 'log' } })}
      >
        <Text style={[styles.logWorkoutText, { color: c.accentForeground }]}>Log Workout →</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

/* ── Pro Analytics Sub-component ── */
interface ProAnalyticsSectionProps {
  data: AnalyticsData | null;
  freeData: AnalyticsData | null;
  timeRange: TimeRange;
  setTimeRange: (r: TimeRange) => void;
  selectedExercise: string;
  setSelectedExercise: (e: string) => void;
  selectedMetric: MetricType;
  setSelectedMetric: (m: MetricType) => void;
  exerciseData: ExerciseDataPoint[];
  showExercisePicker: boolean;
  setShowExercisePicker: (b: boolean) => void;
  exerciseSearch: string;
  setExerciseSearch: (s: string) => void;
  allExerciseNames: string[];
  duelStats: DuelStats | null;
  strengthKg: number | undefined | null;
  hasStrength: boolean;
  displayUnits: string;
  renderLineChart: (dataPoints: ExerciseDataPoint[], title: string, color: string) => React.ReactNode;
  c: any;
  chartWidth: number;
  chartHeight: number;
  profileId: string | null;
  tier: Tier;
  trainingSplit: TrainingSplitResult[];
}

function ProAnalyticsSection({
  data, freeData, timeRange, setTimeRange,
  selectedExercise, setSelectedExercise, selectedMetric, setSelectedMetric,
  exerciseData, showExercisePicker, setShowExercisePicker,
  exerciseSearch, setExerciseSearch, allExerciseNames,
  duelStats, strengthKg, hasStrength, displayUnits,
  renderLineChart, c, chartWidth, chartHeight,
  profileId, tier, trainingSplit,
}: ProAnalyticsSectionProps) {
  const router = useRouter();
  const effectiveData = data || freeData;
  if (!effectiveData) return null;

  const exerciseListForPicker = effectiveData.exerciseList;

  return (
    <View>
      <TutorialOverlay screenKey="pro_upgrade" />
      {/* ── TIME RANGE SELECTOR ── */}
      <View style={[styles.chartCard, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.timeRangeContainer}>
          {(['week', 'month', 'quarter', 'year', 'lifetime'] as TimeRange[]).map((range) => (
            <TouchableOpacity
              key={range}
              style={[styles.timeRangeButton, {
                backgroundColor: timeRange === range ? c.accent : c.surfaceVariant,
                borderColor: timeRange === range ? c.accent : c.border,
              }]}
              onPress={() => setTimeRange(range)}>
              <Text style={[styles.timeRangeText, { color: timeRange === range ? c.text : c.textSecondary }]}>
                {getTimeRangeLabel(range)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Volume Over Time (pro: with time range) */}
        {data && (
          <>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <Text style={[styles.chartTitle, { color: c.text, marginBottom: 0 }]}>Volume Over Time</Text>
              <Text style={{ fontSize: 18, fontWeight: '900', color: c.accent }}>
                {Math.round(data.totalVolume).toLocaleString()} {displayUnits}
              </Text>
            </View>
            <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 8 }}>
              {getTimeRangeLabel(timeRange)} total
            </Text>
            {renderLineChart(
              data.volumeByDay.map(item => ({ date: item.date, value: item.volume })),
              '',
              c.accent,
            )}
          </>
        )}
      </View>

      {/* ── EXERCISE DEEP DIVE ── */}
      {exerciseListForPicker.length > 0 && (
        <View style={[styles.chartCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, marginBottom: 12 }]}>Exercise Deep Dive</Text>
          <View style={styles.exerciseControls}>
            <TouchableOpacity
              style={[styles.exerciseSelector, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}
              onPress={() => { setShowExercisePicker(!showExercisePicker); setExerciseSearch(''); }}>
              <Text style={[styles.exerciseSelectorText, { color: c.text }]}>{selectedExercise || 'Select Exercise'}</Text>
              <ChevronDown size={20} color={c.textSecondary} />
            </TouchableOpacity>

            <View style={styles.metricButtons}>
              {(['topSet', 'estimated1RM', 'totalVolume'] as MetricType[]).map((metric) => (
                <TouchableOpacity
                  key={metric}
                  style={[styles.metricButton, {
                    backgroundColor: selectedMetric === metric ? c.warning : c.surfaceVariant,
                    borderColor: selectedMetric === metric ? c.warning : c.border,
                  }]}
                  onPress={() => setSelectedMetric(metric)}>
                  <Text style={[styles.metricButtonText, { color: selectedMetric === metric ? c.text : c.textSecondary }]}>
                    {metric === 'topSet' ? 'Top Set' : metric === 'estimated1RM' ? '1RM Estimate' : 'Volume'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {showExercisePicker && (() => {
            const merged = Array.from(new Set([...exerciseListForPicker, ...allExerciseNames])).sort();
            const query = exerciseSearch.trim().toLowerCase();
            const filtered = query ? merged.filter(e => e.toLowerCase().includes(query)) : merged;
            return (
              <View style={[styles.exercisePicker, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
                <TextInput
                  style={[styles.exerciseSearchInput, { color: c.text, borderColor: c.border }]}
                  placeholder="Search exercises..."
                  placeholderTextColor={c.textTertiary}
                  value={exerciseSearch}
                  onChangeText={setExerciseSearch}
                  autoFocus
                />
                <ScrollView style={{ maxHeight: 180 }} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                  {filtered.map((exercise) => (
                    <TouchableOpacity
                      key={exercise}
                      style={[styles.exerciseOption, { borderBottomColor: c.border }]}
                      onPress={() => { setSelectedExercise(exercise); setShowExercisePicker(false); setExerciseSearch(''); }}>
                      <Text style={[styles.exerciseOptionText, { color: c.text }]}>{exercise}</Text>
                      {selectedExercise === exercise && <Award size={16} color={c.accent} />}
                    </TouchableOpacity>
                  ))}
                  {filtered.length === 0 && (
                    <Text style={{ padding: 12, color: c.textSecondary, fontSize: 13, fontStyle: 'italic' }}>No exercises found</Text>
                  )}
                </ScrollView>
              </View>
            );
          })()}

          {renderLineChart(
            exerciseData,
            `${selectedExercise} - ${({ topSet: `Top Set (${displayUnits})`, estimated1RM: `1RM Estimate (${displayUnits})`, totalVolume: `Volume (${displayUnits})` })[selectedMetric]}`,
            c.warning,
          )}
        </View>
      )}

      {/* ── PR Progression Charts ── */}
      {profileId && (() => {
        const isPro = hasAtLeast(tier, 'pro');
        const CORE_LIFTS = [
          { key: 'bench', label: 'Bench Press' },
          { key: 'squat', label: 'Back Squat' },
          { key: 'deadlift', label: 'Deadlift' },
          { key: 'weighted_pull_up', label: 'Pull-Ups' },
          { key: 'dead_hang', label: 'Dead Hang' },
        ];
        return isPro ? (
          <View style={[styles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={styles.sectionHeader}>
              <Trophy size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>PR Progression</Text>
            </View>
            {CORE_LIFTS.map((lift) => (
              <View key={lift.key}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: c.textSecondary, marginTop: 8 }}>
                  {lift.label}
                </Text>
                <PRProgressionChart
                  exerciseName={lift.label}
                  userId={profileId!}
                  height={100}
                  unit={displayUnits === 'kg' ? 'kg' : 'lbs'}
                />
              </View>
            ))}
          </View>
        ) : (
          <TouchableOpacity
            style={[styles.sectionCard, { backgroundColor: c.surface, borderColor: c.accent, opacity: 0.8 }]}
            onPress={() => router.push('/paywall')}
            activeOpacity={0.7}
          >
            <View style={styles.sectionHeader}>
              <Trophy size={16} color={c.accent} />
              <Text style={[styles.sectionTitle, { color: c.text }]}>🔒 PR Progression</Text>
            </View>
            <Text style={{ fontSize: 13, color: c.textSecondary, lineHeight: 18 }}>
              Track your strength progress over time with detailed PR progression charts for each core lift.
            </Text>
            <Text style={{ fontSize: 14, fontWeight: '700', color: c.accent, marginTop: 4 }}>
              Upgrade to Pro →
            </Text>
          </TouchableOpacity>
        );
      })()}

      {/* ── Training Split ── */}
      {trainingSplit.length > 0 && (
        <View style={[styles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={styles.sectionHeader}>
            <Activity size={16} color={c.accent} />
            <Text style={[styles.sectionTitle, { color: c.text }]}>Training Split</Text>
            <Text style={{ fontSize: 11, fontWeight: '500', color: c.textTertiary, marginLeft: 'auto' }}>Last 30 days</Text>
          </View>
          {trainingSplit.filter((item) => item.percentage > 0).map((item) => (
            <View key={item.pattern} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
              <Text style={{ width: 44, fontSize: 11, fontWeight: '700', color: c.textSecondary, letterSpacing: 0.3 }}>{item.pattern}</Text>
              <View style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(128,128,128,0.15)', overflow: 'hidden' as const }}>
                <View style={{ height: '100%' as any, borderRadius: 4, backgroundColor: c.accent, width: `${Math.max(item.percentage, 2)}%` as any }} />
              </View>
              <Text style={{ width: 32, fontSize: 11, fontWeight: '700', color: c.text, textAlign: 'right' as const }}>{item.percentage}%</Text>
            </View>
          ))}
        </View>
      )}

      {/* ── PRO INSIGHTS ── */}
      <View style={[styles.insightsCard, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.insightsTitle, { color: c.textTertiary }]}>PRO INSIGHTS</Text>

        <View style={styles.insightRow}>
          <Text style={[styles.insightLabel, { color: c.textSecondary }]}>Strength Score</Text>
          <Text style={[styles.insightValue, { color: c.text }]}>
            {hasStrength ? `${Math.round(strengthKg!)}` : 'Not Yet Ranked'}
          </Text>
        </View>

        <View style={styles.insightRow}>
          <Text style={[styles.insightLabel, { color: c.textSecondary }]}>Duel Record</Text>
          <Text style={[styles.insightValue, { color: c.text }]}>
            {duelStats ? `${duelStats.wins}W - ${duelStats.losses}L (${duelStats.win_rate_percentage}%)` : '—'}
          </Text>
        </View>

        {effectiveData.avgRPE != null && (
          <View style={styles.insightRow}>
            <Text style={[styles.insightLabel, { color: c.textSecondary }]}>Avg. Perceived Effort</Text>
            <Text style={[styles.insightValue, { color: c.text }]}>{effectiveData.avgRPE}/10</Text>
          </View>
        )}

        <View style={styles.insightRow}>
          <Text style={[styles.insightLabel, { color: c.textSecondary }]}>Avg. Volume/Session</Text>
          <Text style={[styles.insightValue, { color: c.text }]}>
            {effectiveData.totalWorkouts > 0 ? Math.round(effectiveData.totalVolume / effectiveData.totalWorkouts).toLocaleString() : '—'}
          </Text>
        </View>

        <View style={styles.insightRow}>
          <Text style={[styles.insightLabel, { color: c.textSecondary }]}>Avg. Exercises/Session</Text>
          <Text style={[styles.insightValue, { color: c.text }]}>
            {effectiveData.totalWorkouts > 0 ? (effectiveData.totalExercises / effectiveData.totalWorkouts).toFixed(1) : '—'}
          </Text>
        </View>
      </View>

      {/* ── ADVANCED PRO ANALYTICS ── */}
      <AdvancedProAnalytics profileId={profileId} displayUnits={displayUnits} c={c} timeRange={timeRange} renderLineChart={renderLineChart} />
    </View>
  );
}

/* ── Advanced Pro Analytics Sub-component ── */
interface AdvancedProAnalyticsProps {
  profileId: string | null;
  displayUnits: string;
  c: any;
  timeRange: TimeRange;
  renderLineChart: (dataPoints: ExerciseDataPoint[], title: string, color: string) => React.ReactNode;
}

function AdvancedProAnalytics({ profileId, displayUnits, c, timeRange, renderLineChart }: AdvancedProAnalyticsProps) {
  const [weeklyVolume, setWeeklyVolume] = useState<ExerciseDataPoint[]>([]);
  const [muscleBalance, setMuscleBalance] = useState<{ group: string; count: number; pct: number }[]>([]);
  const [weeklyFrequency, setWeeklyFrequency] = useState<ExerciseDataPoint[]>([]);
  const [avgRestDays, setAvgRestDays] = useState<number | null>(null);
  const [prVelocity, setPrVelocity] = useState<ExerciseDataPoint[]>([]);

  useEffect(() => {
    if (!profileId) return;
    loadAdvancedAnalytics();
  }, [profileId, timeRange]);

  const loadAdvancedAnalytics = async () => {
    if (!profileId) return;
    const { startUtcIso, endUtcIsoExclusive } = getAnalyticsDateRange(timeRange);

    let query = supabase.from('workouts').select('*').eq('user_id', profileId);
    if (startUtcIso) query = query.gte('performed_at', startUtcIso);
    query = query.lt('performed_at', endUtcIsoExclusive);
    const { data: workouts } = await query.order('performed_at', { ascending: true });
    if (!workouts || workouts.length === 0) return;

    // ── Weekly Volume Trend ──
    const volByWeek: Record<string, number> = {};
    const freqByWeek: Record<string, number> = {};
    const muscleMap: Record<string, number> = {};
    const workoutDates: Date[] = [];

    for (const workout of workouts as any[]) {
      const perfDate = new Date(workout.performed_at);
      workoutDates.push(perfDate);
      const weekKey = getWeekKey(perfDate);
      const payload = typeof workout.payload === 'string' ? JSON.parse(workout.payload) : workout.payload;

      const vol = calculateWorkoutVolume(workout.payload, displayUnits);
      volByWeek[weekKey] = (volByWeek[weekKey] || 0) + vol;
      freqByWeek[weekKey] = (freqByWeek[weekKey] || 0) + 1;

      for (const exercise of (payload?.exercises ?? [])) {
        // First try to look up in local exercise database for accurate muscle mapping
        const dbInfo = exercise.name ? lookupExerciseInfo(exercise.name) : undefined;
        let group: string;
        if (dbInfo) {
          group = normalizeBodyPart(dbInfo.primaryMuscle);
        } else {
          const bodyPart = (exercise.body_part || exercise.bodyPart || 'Other').toLowerCase();
          group = normalizeBodyPart(bodyPart);
        }
        muscleMap[group] = (muscleMap[group] || 0) + 1;
      }
    }

    // Weekly volume chart data
    setWeeklyVolume(
      Object.entries(volByWeek)
        .map(([date, value]) => ({ date, value: Math.round(value) }))
        .sort((a, b) => a.date.localeCompare(b.date))
    );

    // Training frequency chart data
    setWeeklyFrequency(
      Object.entries(freqByWeek)
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => a.date.localeCompare(b.date))
    );

    // Muscle group balance
    const totalExCount = Object.values(muscleMap).reduce((a, b) => a + b, 0);
    setMuscleBalance(
      Object.entries(muscleMap)
        .map(([group, count]) => ({ group, count, pct: totalExCount > 0 ? Math.round((count / totalExCount) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)
    );

    // Rest day analysis
    if (workoutDates.length >= 2) {
      const sorted = workoutDates.sort((a, b) => a.getTime() - b.getTime());
      let totalGap = 0;
      let gapCount = 0;
      for (let i = 1; i < sorted.length; i++) {
        const gap = (sorted[i].getTime() - sorted[i - 1].getTime()) / (1000 * 60 * 60 * 24);
        if (gap >= 0.5) { // At least half a day gap to count
          totalGap += gap;
          gapCount++;
        }
      }
      setAvgRestDays(gapCount > 0 ? Math.round((totalGap / gapCount) * 10) / 10 : null);
    }

    // PR velocity — count PRs per month from strength_events
    const { data: prEvents } = await supabase
      .from('strength_events')
      .select('performed_at, is_pr')
      .eq('profile_id', profileId)
      .eq('is_pr', true)
      .order('performed_at', { ascending: true });

    if (prEvents && prEvents.length > 0) {
      const prByMonth: Record<string, number> = {};
      for (const ev of prEvents as any[]) {
        const d = new Date(ev.performed_at);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        prByMonth[monthKey] = (prByMonth[monthKey] || 0) + 1;
      }
      setPrVelocity(
        Object.entries(prByMonth)
          .map(([date, value]) => ({ date, value }))
          .sort((a, b) => a.date.localeCompare(b.date))
      );
    }
  };

  function getWeekKey(d: Date): string {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
  }

  function normalizeBodyPart(part: string): string {
    const p = part.trim().toLowerCase();
    if (p.includes('chest') || p.includes('pec')) return 'Chest';
    if (p.includes('back') || p.includes('lat') || p.includes('row')) return 'Back';
    if (p.includes('shoulder') || p.includes('delt')) return 'Shoulders';
    if (p.includes('leg') || p.includes('quad') || p.includes('hamstring') || p.includes('glute') || p.includes('squat')) return 'Legs';
    if (p.includes('arm') || p.includes('bicep') || p.includes('tricep')) return 'Arms';
    if (p.includes('core') || p.includes('ab')) return 'Core';
    if (p.includes('cardio')) return 'Cardio';
    return 'Other';
  }

  return (
    <View>
      {/* ── Weekly Volume Trend ── */}
      {weeklyVolume.length > 1 && (
        <View style={[advStyles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={advStyles.sectionHeader}>
            <TrendingUp size={16} color={c.accent} />
            <Text style={[advStyles.sectionTitle, { color: c.text }]}>Weekly Volume Trend</Text>
          </View>
          <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 8 }}>
            Total weight × reps × sets per week ({displayUnits})
          </Text>
          {renderLineChart(weeklyVolume, '', c.accent)}
        </View>
      )}

      {/* ── Muscle Group Balance ── */}
      {muscleBalance.length > 0 && (
        <View style={[advStyles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={advStyles.sectionHeader}>
            <Target size={16} color={c.accent} />
            <Text style={[advStyles.sectionTitle, { color: c.text }]}>Muscle Group Balance</Text>
          </View>
          {muscleBalance.slice(0, 8).map((item) => (
            <View key={item.group} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
              <Text style={{ width: 72, fontSize: 12, fontWeight: '700', color: c.textSecondary }}>{item.group}</Text>
              <View style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: 'rgba(128,128,128,0.15)', overflow: 'hidden' as const }}>
                <View style={{ height: '100%' as any, borderRadius: 4, backgroundColor: c.accent, width: `${Math.max(item.pct, 2)}%` as any }} />
              </View>
              <Text style={{ width: 36, fontSize: 11, fontWeight: '700', color: c.text, textAlign: 'right' as const }}>{item.pct}%</Text>
            </View>
          ))}
          {muscleBalance.length > 0 && (() => {
            const least = muscleBalance[muscleBalance.length - 1];
            const most = muscleBalance[0];
            return least.group !== most.group ? (
              <Text style={{ fontSize: 11, color: c.textTertiary, marginTop: 8, fontStyle: 'italic' }}>
                💡 Most trained: {most.group} ({most.pct}%) · Least: {least.group} ({least.pct}%)
              </Text>
            ) : null;
          })()}
        </View>
      )}

      {/* ── Training Frequency ── */}
      {weeklyFrequency.length > 1 && (
        <View style={[advStyles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={advStyles.sectionHeader}>
            <Calendar size={16} color={c.accent} />
            <Text style={[advStyles.sectionTitle, { color: c.text }]}>Training Frequency</Text>
          </View>
          <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 8 }}>
            Workouts per week
          </Text>
          {renderLineChart(weeklyFrequency, '', '#3b82f6')}
          {(() => {
            const avg = weeklyFrequency.reduce((s, w) => s + w.value, 0) / weeklyFrequency.length;
            return (
              <Text style={{ fontSize: 12, fontWeight: '700', color: c.textSecondary, marginTop: 8 }}>
                Average: {avg.toFixed(1)} workouts/week
              </Text>
            );
          })()}
        </View>
      )}

      {/* ── Rest Day Analysis ── */}
      {avgRestDays !== null && (
        <View style={[advStyles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={advStyles.sectionHeader}>
            <Clock size={16} color={c.accent} />
            <Text style={[advStyles.sectionTitle, { color: c.text }]}>Rest Day Analysis</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
            <Text style={{ fontSize: 28, fontWeight: '900', color: c.text }}>{avgRestDays}</Text>
            <Text style={{ fontSize: 14, fontWeight: '600', color: c.textSecondary }}>days between sessions (avg)</Text>
          </View>
          <Text style={{ fontSize: 11, color: c.textTertiary, marginTop: 8, fontStyle: 'italic' }}>
            {avgRestDays < 1.5
              ? '⚠️ You might be training very frequently — make sure you are recovering enough.'
              : avgRestDays <= 2.5
              ? '✅ Good recovery balance between sessions.'
              : avgRestDays <= 4
              ? '💡 Consider increasing training frequency for faster progress.'
              : '⚠️ Long gaps between sessions — try to be more consistent.'}
          </Text>
        </View>
      )}

      {/* ── PR Velocity ── */}
      {prVelocity.length > 1 && (
        <View style={[advStyles.sectionCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <View style={advStyles.sectionHeader}>
            <Flame size={16} color={'#f59e0b'} />
            <Text style={[advStyles.sectionTitle, { color: c.text }]}>PR Velocity</Text>
          </View>
          <Text style={{ fontSize: 11, color: c.textTertiary, marginBottom: 8 }}>
            Personal records per month
          </Text>
          {renderLineChart(prVelocity, '', '#f59e0b')}
          {(() => {
            const total = prVelocity.reduce((s, m) => s + m.value, 0);
            const avg = total / prVelocity.length;
            return (
              <Text style={{ fontSize: 12, fontWeight: '700', color: c.textSecondary, marginTop: 8 }}>
                {total} PRs total · {avg.toFixed(1)}/month average
              </Text>
            );
          })()}
        </View>
      )}
    </View>
  );
}

const advStyles = StyleSheet.create({
  sectionCard: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 20 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: '700' },
});

const styles = StyleSheet.create({
  container: { flex: 1 },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { padding: 16, paddingBottom: 40 },
  timeRangeContainer: { flexDirection: 'row', gap: 8, marginBottom: 20 },
  timeRangeButton: { flex: 1, paddingVertical: 10, paddingHorizontal: 8, borderRadius: 0, borderWidth: 1, alignItems: 'center' },
  timeRangeText: { fontSize: 12, fontWeight: '700' },
  statsGrid: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  statCard: { flex: 1, borderWidth: 1, borderRadius: 2, padding: 14, alignItems: 'center', gap: 6 },
  statValue: { fontSize: 18, fontWeight: '900' },
  statLabel: { fontSize: 11, textAlign: 'center' },
  chartCard: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 20 },
  chartContainer: { marginTop: 8 },
  chartTitle: { fontSize: 16, fontWeight: '700', marginBottom: 16 },
  sectionCard: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 20 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: '700' },
  prRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  prExercise: { fontSize: 14, fontWeight: '600' },
  prDate: { fontSize: 11, marginTop: 2 },
  prValue: { fontSize: 14, fontWeight: '800' },
  freqRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth },
  freqName: { fontSize: 14, fontWeight: '600' },
  freqBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 },
  freqCount: { fontSize: 13, fontWeight: '700' },
  exerciseControls: { gap: 12, marginBottom: 16 },
  exerciseSelector: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderRadius: 2, borderWidth: 1 },
  exerciseSelectorText: { fontSize: 15, fontWeight: '600' },
  exercisePicker: { borderRadius: 2, borderWidth: 1, marginBottom: 16, overflow: 'hidden' as const },
  exerciseSearchInput: { paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, borderBottomWidth: 1 },
  exerciseOption: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderBottomWidth: 1 },
  exerciseOptionText: { fontSize: 15, fontWeight: '600' },
  metricButtons: { flexDirection: 'row', gap: 8 },
  metricButton: { flex: 1, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 0, borderWidth: 1, alignItems: 'center' },
  metricButtonText: { fontSize: 13, fontWeight: '700' },
  emptyChartContainer: { height: 220, justifyContent: 'center', alignItems: 'center' },
  emptyChartText: { fontSize: 14, fontStyle: 'italic' },
  insightsCard: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 20 },
  insightsTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 1, marginBottom: 12 },
  insightRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: 'rgba(128,128,128,0.15)' },
  insightLabel: { fontSize: 13, fontWeight: '500' },
  insightValue: { fontSize: 13, fontWeight: '700' },
  logWorkoutButton: { paddingVertical: 16, borderRadius: 2, alignItems: 'center', marginTop: 8, marginBottom: 20 },
  logWorkoutText: { fontSize: 16, fontWeight: '800' },
});
